import { Alert, Box, Button, Typography } from "@mui/material";
import { useTranslation } from "i18n-fe";
// Card #1992 AC7 - a ruling's `at` is a genuine server instant
// (new Date().toISOString(), not an FVD Access stamp), so it renders through
// common-fe's venueTime on the show's real venueTimezone. The classDisplay.js
// local formatVenueTime is NOT that: it is reserved for FVD-sourced
// scheduledTime/rideTime (#1811).
// #2097 - ScoringForm passes selectedShow.venueTimezone down as a prop; when
// the show has no usable zone the banner says so instead of printing a clean,
// plausible Pacific time (it used to omit `timeZone` and silently fall back).
import { formatVenueTimeOrUnset } from "common-fe";
import { resolveRulingAlert } from "../util/rideRuling";

// Card #3075 - the ride-level No Show (#1992), Excused (#2064) and Withdrawn (#3144) alert
// banners, pulled out of ScoringForm.jsx so they can be rendered and tested
// on their own. The menu items and confirm dialog that used to live here are
// now the End Ride flow (card #2798, EndRideParts.jsx).

const KINDS = {
  noShow: {
    bannerTestId: "no-show-alert-banner",
    headerTestId: "no-show-alert-header",
    undoTestId: "no-show-undo-button",
    alertHeaderKey: "scoring.noShowAlertHeader",
    alertBodyKey: "scoring.noShowAlertBody",
    undoKey: "scoring.noShowUndoButton",
    undoErrorKey: "scoring.gavelRideNoShowUndoError",
  },
  excused: {
    bannerTestId: "excused-alert-banner",
    headerTestId: "excused-alert-header",
    undoTestId: "excused-undo-button",
    alertHeaderKey: "scoring.excusedAlertHeader",
    alertBodyKey: "scoring.excusedAlertBody",
    undoKey: "scoring.excusedUndoButton",
    undoErrorKey: "scoring.gavelRideExcusedUndoError",
  },
  // Card #3144: Withdrawn (#2795) had no banner, so a sibling seat never saw it.
  withdrawn: {
    bannerTestId: "withdrawn-alert-banner",
    headerTestId: "withdrawn-alert-header",
    undoTestId: "withdrawn-undo-button",
    alertHeaderKey: "scoring.withdrawnAlertHeader",
    alertBodyKey: "scoring.withdrawnAlertBody",
    undoKey: "scoring.withdrawnUndoButton",
    undoErrorKey: "scoring.gavelRideWithdrawnUndoError",
  },
};

// Renders on EVERY panel position's sheet for this ride while the record is
// active (delivered by useRideNoShow/useRideExcused's poll). "Who" is the
// resolved judge name, never the raw sub (see resolveRulingAlert); Undo is
// only rendered for the position that could have caused it (C).
function RideRulingAlertBanner({ kind, record, klass, judgePosition, venueTimezone, onUndo, undoing, undoError }) {
  const { t } = useTranslation("score");
  const cfg = KINDS[kind];
  const alert = resolveRulingAlert(record, klass, judgePosition);
  if (!alert) return null;
  const byName =
    alert.who.kind === "name"
      ? alert.who.name
      : alert.who.kind === "position"
        ? t("scoring.noShowByPositionFallback", { position: alert.who.position })
        : t("scoring.noShowByUnknown");
  const whenLabel = formatVenueTimeOrUnset(alert.at, {
    timeZone: venueTimezone,
    unsetText: t("scoring.alertWhenUnset"),
  });
  return (
    <Box
      data-testid={cfg.bannerTestId}
      sx={{
        mb: 2,
        p: 1.5,
        bgcolor: "background.paper",
        border: 1,
        borderColor: "error.main",
        borderRadius: 2,
      }}
    >
      <Typography
        data-testid={cfg.headerTestId}
        variant="overline"
        sx={{
          display: "block",
          mb: 0.5,
          fontWeight: 700,
          lineHeight: 1.2,
          color: "error.main",
        }}
      >
        {t(cfg.alertHeaderKey)}
      </Typography>
      <Typography variant="body2" sx={{ mb: alert.canUndo ? 1 : 0 }}>
        {t(cfg.alertBodyKey, { who: byName, when: whenLabel })}
      </Typography>
      {undoError && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {t(cfg.undoErrorKey)}
        </Alert>
      )}
      {alert.canUndo && (
        <Button
          size="small"
          variant="outlined"
          color="error"
          loading={undoing}
          onClick={onUndo}
          data-testid={cfg.undoTestId}
        >
          {t(cfg.undoKey)}
        </Button>
      )}
    </Box>
  );
}

export function NoShowAlertBanner({ noShow, ...rest }) {
  return <RideRulingAlertBanner kind="noShow" record={noShow} {...rest} />;
}

export function ExcusedAlertBanner({ excused, ...rest }) {
  return <RideRulingAlertBanner kind="excused" record={excused} {...rest} />;
}

export function WithdrawnAlertBanner({ withdrawn, ...rest }) {
  return <RideRulingAlertBanner kind="withdrawn" record={withdrawn} {...rest} />;
}

// #2792 AC6 -- the ride's term reason on EVERY sheet. A sibling judge's
// elimination terminates the ride for this sheet too, but the elimination
// itself lives on that judge's sheet (never copied onto this one), so without
// this banner this judge sees an ordinary in-progress sheet with no hint why
// the ride ended. Shown only when the ride is elimination-terminated and THIS
// sheet carries no elimination of its own (the sheet's own "Eliminated" block
// already says so).
export function RideTerminatedAlertBanner({ reason }) {
  const { t } = useTranslation("score");
  if (!reason) return null;
  return (
    <Box
      data-testid="ride-terminated-banner"
      sx={{
        mb: 2,
        p: 1.5,
        bgcolor: "background.paper",
        border: 1,
        borderColor: "error.main",
        borderRadius: 2,
      }}
    >
      <Typography
        data-testid="ride-terminated-banner-header"
        variant="overline"
        sx={{ display: "block", mb: 0.5, fontWeight: 700, lineHeight: 1.2, color: "error.main" }}
      >
        {t("scoring.rideTerminatedAlertHeader", { reason })}
      </Typography>
      <Typography variant="body2">{t("scoring.rideTerminatedAlertBody")}</Typography>
    </Box>
  );
}
